import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TabTitle } from "../../../util/titleChanger";
import { GlobalState } from "../../../GlobalState";
import { createNewInvoice, getOrdersByDateRange, updateOrder } from "../../../action";
import { toast, ToastContainer } from "react-toastify";
import ReactPaginate from "react-paginate";
import { debounce } from "lodash";
import "./OrderDashboard.css";
import PrintModal from "../../Print/PrintModal";

const OrderRow = React.memo(({ order, onStatusUpdate, onPrintClick }) => (
  <tr key={order.id}>
    <td>{order.id}</td>
    <td>{order.customer?.name || "N/A"}</td>
    <td>{new Date(order.date).toLocaleDateString()}</td>
    <td>{order.total_qty}</td>
    <td>Rs {parseFloat(order.total_amount || 0).toFixed(2)}</td>
    <td>
      <select className="select-box" value={order.status} onChange={(e) => onStatusUpdate(order.id, e.target.value)} aria-label={`Update status for order ${order.id}`}>
        <option value="Pending">Pending</option>
        <option value="Shipped">Shipped</option>
        <option value="Completed">Completed</option>
        <option value="Cancelled">Cancelled</option>
      </select>
    </td>
    <td>
      <button className="table_section_btn" onClick={() => onPrintClick(order)}>
        <i className="uil uil-print"></i>
      </button>
      <Link to={`/ordered-product/${order.id}`}>
        <button>
          <i className="uil uil-eye"></i>
        </button>
      </Link>
    </td>
  </tr>
));

const OrderFilters = ({ search, onSearchChange, startDate, endDate, onDateChange, status, onStatusChange, onResetFilters }) => (
  <div className="filter-controls table-right">
    <input type="text" placeholder="Search by order ID, customer name, or customer code..." value={search} onChange={onSearchChange} />
    <div className="date-filters">
      <label>
        Start Date:
        <input type="date" value={startDate} max={endDate || new Date().toISOString().split("T")[0]} onChange={(e) => onDateChange(e, "start")} />
      </label>
      <label>
        End Date:
        <input type="date" value={endDate} min={startDate} max={new Date().toISOString().split("T")[0]} onChange={(e) => onDateChange(e, "end")} />
      </label>
      <label>
        Status:
        <select className="select-box order-select" value={status} onChange={onStatusChange} aria-label="Filter by order status">
          <option value="">All Status</option>
          <option value="Pending">Pending</option>
          <option value="Shipped">Shipped</option>
          <option value="Completed">Completed</option>
          <option value="Cancelled">Cancelled</option>
        </select>
      </label>
    </div>
    <button onClick={onResetFilters}>Reset Filters</button>
  </div>
);

export default function OrderDashboard() {
  TabTitle("Order Dashboard");

  const state = useContext(GlobalState);
  const [allOrders, setAllOrders] = state.orderAPI.allOrders;
  const [rowPerPage] = state.orderAPI.rowPerPage;
  const [pageNumber, setPageNumber] = state.orderAPI.pageNumber;
  const [totalPages, setTotalPages] = state.orderAPI.totalPages;
  const [search, setSearch] = state.orderAPI.search;
  const [status, setStatus] = state.orderAPI.status;
  const [callback, setCallback] = state.orderAPI.callback;

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [printOrder, setPrintOrder] = useState(null);
  const signInUser = JSON.parse(localStorage.getItem("user_data"));

  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      await getOrdersByDateRange(rowPerPage, pageNumber, search, status, startDate, endDate, (res) => {
        if (res.status === 200) {
          setAllOrders(Array.isArray(res.data?.data) ? res.data.data : []);
          setTotalPages(res.data?.totalPage || 1);
        } else {
          setAllOrders([]);
          setTotalPages(1);
          setError(res.data?.message || "Failed to fetch orders");
        }
      });
    } catch (err) {
      setAllOrders([]);
      setError("An error occurred while fetching orders");
    } finally {
      setIsLoading(false);
    }
  }, [rowPerPage, pageNumber, search, startDate, endDate, status]);

  const debouncedSearch = useCallback(
    debounce(() => {
      setPageNumber(1);
      fetchOrders();
    }, 500),
    [fetchOrders]
  );

  useEffect(() => {
    fetchOrders();
  }, [pageNumber, rowPerPage, status, startDate, endDate]);

  useEffect(() => {
    debouncedSearch();
    return () => debouncedSearch.cancel();
  }, [search, debouncedSearch]);

  const pageNumberHandler = useCallback((data) => {
    setPageNumber(data.selected + 1);
  }, []);

  const searchHandler = useCallback((e) => {
    setSearch(e.target.value);
  }, []);

  const statusHandler = useCallback((e) => {
    setStatus(e.target.value);
    setPageNumber(1);
  }, []);

  const handleDateChange = useCallback((e, type) => {
    if (type === "start") {
      setStartDate(e.target.value);
    } else {
      setEndDate(e.target.value);
    }
    setPageNumber(1);
  }, []);

  const handleStatusUpdate = useCallback(
    async (orderId, status) => {
      try {
        const orderInfo = { orderId, status };
        await updateOrder(orderInfo, (response) => {
          if (response.status === 200) {
            toast.success(response.data.message);
            fetchOrders();
          } else {
            toast.error(response.data.message);
          }
        });
      } catch (error) {
        toast.error("An error occurred while updating the order");
      }
    },
    [fetchOrders]
  );

  const resetFilters = useCallback(() => {
    setSearch("");
    setStatus("");
    setStartDate("");
    setEndDate("");
    setPageNumber(1);
  }, []);

  const handlePrintClick = (order) => {
    setPrintOrder(order);
  };

  // Add this above your component
  const getAldiReceiptStyles = () => `
@page {
  size: 80mm auto;
  margin: 0;
}
body {
  width: 80mm !important;
  font-family: 'Courier New', monospace !important;
  font-size: 12px !important;
  padding: 5mm !important;
  background: white !important;
  color: black !important;
  -webkit-print-color-adjust: exact !important;
}
.aldi-receipt {
  width: 100%;
}
.aldi-header {
  text-align: center;
  font-weight: bold;
  margin-bottom: 10px;
}
.aldi-divider {
  border-top: 1px dashed #000;
  margin: 5px 0;
}
.aldi-item-row {
  display: flex;
  justify-content: space-between;
  margin: 3px 0;
}
.aldi-total {
  font-weight: bold;
  margin-top: 10px;
}
`;

  const generateAldiReceiptHTML = (order) => `
    <div class="header">
      <h2>YOUR STORE</h2>
      <p>123 Example St, City</p>
    </div>
    <div class="divider"></div>
    <p>Order #: ${order.invoice_no}</p>
    <p>Date: ${new Date(order.date).toLocaleString()}</p>
    <p>Customer: ${order.customer?.name || "Walk-in"}</p>
    <div class="divider"></div>
    ${order.items
      ?.map(
        (item) => `
      <div class="item-row">
        <span>${item.quantity}x ${item.product.name}</span>
        <span>Rs ${item.total_price.toFixed(2)}</span>
      </div>
    `
      )
      .join("")}
    <div class="divider"></div>
    <div class="item-row total">
      <span>TOTAL:</span>
      <span>Rs ${order.net_price.toFixed(2)}</span>
    </div>
    <div class="footer">Thank you!</div>
  `;

  const handlePrint = async ({ printSize, printer }) => {
    try {
      const invoicePayload = {
        orderId: printOrder.id,
        products: printOrder.items.map((item) => ({
          id: item.product.id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.total_price,
          discount: item.discount || 0,
        })),
        total_price: printOrder.total_amount,
        total_discount: printOrder.total_discount || 0,
        net_price: printOrder.net_price,
        createdBy: signInUser.id || 1,
      };

      await createNewInvoice(invoicePayload, async (res) => {
        if (res.status !== 200) {
          toast.error("Invoice creation failed");
          return;
        }

        const invoiceData = res.response.invoiceData;

        const printContent = `
          <!DOCTYPE html>
          <html>
            <head>
              <title>Invoice #${invoiceData.invoice_no}</title>
              <style>
                ${getAldiReceiptStyles()}
              </style>
            </head>
            <body>
              <div class="aldi-receipt">
                ${generateAldiReceiptHTML(invoiceData)}
              </div>
              <script>
                setTimeout(() => window.print(), 100);
              </script>
            </body>
          </html>
        `;

        const printWindow = window.open("", "_blank");
        printWindow.document.write(printContent);
        printWindow.document.close();

        toast.success("Invoice created and ready to print!");
      });
    } catch (error) {
      toast.error("Printing failed: " + error.message);
    } finally {
      setPrintOrder(null);
    }
  };

  return (
    <div className="form-container">
      <div className="table">
        <div className="table_header">
          <h1>Order Details</h1>
          <OrderFilters
            search={search}
            onSearchChange={searchHandler}
            startDate={startDate}
            endDate={endDate}
            onDateChange={handleDateChange}
            status={status}
            onStatusChange={statusHandler}
            onResetFilters={resetFilters}
          />
        </div>

        {isLoading ? (
          <div className="loading-spinner">
            <p>Loading orders...</p>
          </div>
        ) : error ? (
          <div className="error-message">
            <p>{error}</p>
          </div>
        ) : !Array.isArray(allOrders) || allOrders.length === 0 ? (
          <div className="no-orders-message">
            <p>No orders found matching your criteria.</p>
          </div>
        ) : (
          <div className="table_section">
            <table aria-label="Order details table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th>Quantity</th>
                  <th>Total Value</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {allOrders.map((order) => (
                  <OrderRow key={order.id} order={order} onStatusUpdate={handleStatusUpdate} onPrintClick={handlePrintClick} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <ReactPaginate
            breakLabel="..."
            nextLabel="next >"
            onPageChange={pageNumberHandler}
            marginPagesDisplayed={2}
            pageCount={totalPages}
            previousLabel="< previous"
            containerClassName="pagination"
            pageLinkClassName="page-num"
            previousLinkClassName="page-num"
            nextLinkClassName="page-num"
            activeLinkClassName="active-num"
            forcePage={pageNumber - 1}
          />
        )}
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
      {printOrder && <PrintModal order={printOrder} onClose={() => setPrintOrder(null)} onPrint={handlePrint} />}
    </div>
  );
}
